import type { CampaignOutcomeStatus } from './campaign.schema';

/**
 * Canonical outcome status → label + design-token mapping (SCRUM-73). The SINGLE source of
 * truth for outcome styling across the summary bar (SCRUM-72), the outcome badges (SCRUM-73),
 * and the status dropdown (SCRUM-74), so colors cannot drift between them.
 *
 * Colors use Ryan Lynch's design tokens (src/styles/tokens.css, SCRUM-6) — NO literal hex, and
 * they adapt to dark mode automatically via the `[data-theme]` token values. Note this is a
 * GOLD-accent system: the mockup's "blue" Acquired maps to the real blue token `--indigo`
 * (NOT `--blue`, which is gold), and "gray" No-Response maps to the neutral `--insight-dormant`
 * slate. Badge shape reuses the app's established status-pill pattern (AskBankIQ `AiBadge`).
 *
 * (Kept in its own module so the badge component file exports only a component — satisfies the
 * react-refresh only-export-components rule.)
 */
export const OUTCOME_STATUS_META: Record<
  CampaignOutcomeStatus,
  { label: string; text: string; bg: string }
> = {
  Interested: {
    label: 'Interested',
    text: 'text-[var(--success)]',
    bg: 'bg-[var(--success-dim)]',
  },
  Acquired: {
    label: 'Acquired',
    text: 'text-[var(--indigo)]',
    bg: 'bg-[var(--indigo-dim)]',
  },
  NotInterested: {
    label: 'Not Interested',
    text: 'text-[var(--danger)]',
    bg: 'bg-[var(--danger-dim)]',
  },
  NoResponse: {
    label: 'No Response',
    text: 'text-[var(--insight-dormant)]',
    bg: 'bg-[var(--insight-dormant-dim)]',
  },
  Pending: {
    label: 'Pending',
    text: 'text-[var(--warning)]',
    bg: 'bg-[var(--warning-dim)]',
  },
};

/** Human label for a status (e.g. summary-bar metric labels). */
export function outcomeLabel(status: CampaignOutcomeStatus): string {
  return OUTCOME_STATUS_META[status].label;
}
